import { createTheme } from "@mui/material/styles";

// Paleta oficial Stock Sistema
export const brand = {
  primary: "#1097e6",
  primaryDark: "#0878c7",
  primaryLight: "#55c7ff",
  navy: "#071a33",
  navySoft: "#0c2545",
  slate: "#556273",
  surface: "#dbe5ef",
  surfaceSoft: "#f4f8fc",
  ink: "#031426",
  white: "#ffffff",
};

export const buildTheme = (mode = "light") => {
  const isDark = mode === "dark";

  return createTheme({
    palette: {
      mode,
      primary: {
        main: brand.primary,
        light: brand.primaryLight,
        dark: brand.primaryDark,
        contrastText: brand.white,
      },
      secondary: {
        main: brand.navy,
        light: brand.navySoft,
        dark: brand.ink,
        contrastText: brand.white,
      },
      error: { main: "#ef4444", light: "#f87171", dark: "#dc2626" },
      warning: { main: "#f59e0b", light: "#fbbf24", dark: "#d97706" },
      success: { main: "#10b981", light: "#34d399", dark: "#059669" },
      info: { main: brand.primary, light: brand.primaryLight, dark: brand.primaryDark },
      background: isDark
        ? { default: brand.navy, paper: "#111c33" }
        : { default: "#f5f8fc", paper: brand.white },
      text: isDark
        ? { primary: brand.white, secondary: "#94a3b8" }
        : { primary: brand.ink, secondary: brand.slate },
      divider: isDark ? "rgba(226,232,240,0.12)" : brand.surface,
    },
    typography: {
      fontFamily: [
        '"Inter"',
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        "Roboto",
        '"Helvetica Neue"',
        "Arial",
        "sans-serif",
      ].join(","),
      h1: { fontSize: "2.5rem", fontWeight: 800, letterSpacing: 0 },
      h2: { fontSize: "2rem", fontWeight: 800, letterSpacing: 0 },
      h3: { fontSize: "1.75rem", fontWeight: 800, letterSpacing: 0 },
      h4: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: 0 },
      h5: { fontSize: "1.25rem", fontWeight: 700, letterSpacing: 0 },
      h6: { fontSize: "1rem", fontWeight: 700, letterSpacing: 0 },
      button: { fontWeight: 600, textTransform: "none" },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            background:
              "linear-gradient(180deg, #f8fbff 0%, #eef5fb 100%)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            borderRadius: 8,
            minHeight: 38,
            "&:hover": { boxShadow: "none" },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${brand.primaryLight} 0%, ${brand.primary} 46%, ${brand.primaryDark} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryDark} 100%)`,
            },
          },
          outlined: {
            borderColor: "rgba(16,151,230,0.35)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderColor: isDark ? "rgba(226,232,240,0.08)" : brand.surface,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? "0 1px 2px rgba(0,0,0,0.4)"
              : "0 14px 34px rgba(7,26,51,0.06)",
            borderRadius: 8,
            border: isDark
              ? "1px solid rgba(226,232,240,0.08)"
              : `1px solid ${brand.surface}`,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark
              ? "1px solid rgba(226,232,240,0.08)"
              : `1px solid ${brand.surface}`,
          },
          head: {
            fontWeight: 600,
            color: isDark ? brand.white : brand.navy,
            backgroundColor: isDark ? "#0b1426" : "#eef5fb",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: brand.white,
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(16,151,230,0.55)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: brand.primary,
              borderWidth: 1,
            },
          },
          notchedOutline: {
            borderColor: brand.surface,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
          colorPrimary: {
            backgroundColor: "rgba(16,151,230,0.12)",
            color: brand.primaryDark,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
    },
  });
};

const theme = buildTheme("light");
export default theme;
